/**
 * Auth Bridge API
 * Communicates with LUDOKA backend for session bridge flow (webview auth)
 * Uses direct fetch since no auth token is available during bridge creation/polling
 */

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:3000/api';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: { code: string; message: string };
}

interface BridgeCreateResponse {
  bridgeId: string;
}

interface BridgeStatusResponse {
  status: 'pending' | 'completed' | 'expired';
  accessToken?: string;
  refreshToken?: string;
  user?: Record<string, unknown>;
}

export const authBridgeApi = {
  createBridge: async (): Promise<string> => {
    const res = await fetch(`${API_URL}/auth/bridge`, { method: 'POST' });
    const json = (await res.json()) as ApiResponse<BridgeCreateResponse>;
    if (!json.success || !json.data) throw new Error('Failed to create auth bridge');
    return json.data.bridgeId;
  },

  completeBridge: async (
    bridgeId: string,
    tokens: { accessToken: string; refreshToken: string; user?: Record<string, unknown> }
  ): Promise<void> => {
    const res = await fetch(`${API_URL}/auth/bridge/${bridgeId}/complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(tokens)
    });
    const json = (await res.json()) as ApiResponse<{ message: string }>;
    if (!json.success) throw new Error(json.error?.message ?? 'Failed to complete bridge');
  },

  getBridgeStatus: async (bridgeId: string): Promise<BridgeStatusResponse> => {
    const res = await fetch(`${API_URL}/auth/bridge/${bridgeId}/status`);
    const json = (await res.json()) as ApiResponse<BridgeStatusResponse>;
    if (!json.success || !json.data) throw new Error('Failed to get bridge status');
    return json.data;
  }
};
